import React, { useState, useEffect } from 'react';
import { useChurch } from '../context/ChurchContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCard from '../components/common/StatCard';
import { format, subMonths } from 'date-fns';
import { BarChartIcon } from '../components/common/Icons';
import toast from 'react-hot-toast';
import useChurchSettings from '../hooks/useChurchSettings';

const GENDER_COLORS = ['#3b82f6', '#ec4899', '#f59e0b'];
const STATUS_COLORS_PIE = ['#8b5cf6', '#1e40af', '#10b981', '#f97316'];
const FAMILY_COLORS = ['#7c3aed', '#1e40af', '#0891b2', '#16a34a', '#d97706', '#dc2626', '#db2777', '#4338ca'];

const styles = {
  page: { width: '100%' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '20px' },
  filterBar: {
    backgroundColor: 'var(--card-bg)', borderRadius: '10px', padding: '16px 20px',
    border: '1px solid var(--border)', marginBottom: '20px',
    display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' },
  input: {
    padding: '8px 12px', border: '1px solid var(--border)',
    borderRadius: '7px', fontSize: '0.9rem', color: 'var(--text-primary)',
    background: 'var(--page-bg)', fontFamily: 'inherit',
  },
  select: {
    padding: '8px 12px', border: '1px solid var(--border)',
    borderRadius: '7px', fontSize: '0.9rem', backgroundColor: 'var(--page-bg)',
    color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit',
  },
  btnPrimary: {
    backgroundColor: 'var(--primary)', color: 'white', border: 'none',
    borderRadius: '7px', padding: '9px 20px', cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 700, fontFamily: 'inherit',
  },
  statsGrid: { display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '24px' },
  section: {
    backgroundColor: 'var(--card-bg)', borderRadius: '12px',
    padding: '20px 24px', marginBottom: '20px',
    border: '1px solid var(--border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' },
  tableHead: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
    padding: '8px 12px', backgroundColor: 'var(--page-bg)',
    borderRadius: '6px', marginBottom: '4px',
    fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  tableRow: (idx) => ({
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
    padding: '10px 12px', borderRadius: '6px',
    backgroundColor: idx % 2 === 0 ? 'var(--page-bg)' : 'var(--card-bg)',
    fontSize: '0.88rem', color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border)',
  }),
};

const CUSTOM_LABEL = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Label dédié au pie des familles : utilise le pourcentage déjà calculé par le backend (breakdown[i].percent)
// plutôt que le "percent" fourni par Recharts, pour éviter tout écart de calcul.
const makeFamilyLabel = (breakdown) => ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  const pct = breakdown[index]?.percent ?? 0;
  if (pct < 5) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${Math.round(pct)}%`}
    </text>
  );
};

export default function Statistics() {
  const mob = useIsMobile();
  const { activeChurchId, isPlatformAdmin } = useChurch();
  const churchSettings = useChurchSettings(activeChurchId);
  const [stats, setStats] = useState(null);
  const [familyStats, setFamilyStats] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const firstOfMonth = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [activityType, setActivityType] = useState('');

  useEffect(() => {
    api.get('/activities/types/').then((res) => {
      setActivityTypes(Array.isArray(res.data) ? res.data : res.data.results || []);
    });
  }, [activeChurchId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      let url = `/attendance/statistics/?start_date=${startDate}&end_date=${endDate}`;
      if (activityType) url += `&activity_type=${activityType}`;
      const res = await api.get(url);
      setStats(res.data);
    } catch {
      toast.error('Erreur lors du chargement des statistiques.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, [startDate, endDate, activityType, activeChurchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!churchSettings.use_families) { setFamilyStats(null); return; }
    const params = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
    api.get('/participants/family_stats/', { params })
      .then(res => setFamilyStats(res.data))
      .catch(() => setFamilyStats(null));
  }, [churchSettings.use_families, activeChurchId, isPlatformAdmin]);

  const exportCSV = () => {
    const header = ['Activite', 'Date', 'Type', 'Église', 'Total', 'Hommes', 'Femmes', 'Enfants', 'Nouveaux'];
    const rows = perActivity.map((a) => [
      a.name, a.date, a.activity_type || '', a.church_name || 'Non classé', a.total,
      a.hommes, a.femmes, a.enfants, a.nouveaux,
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistiques-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setPreset = (months) => {
    const end = new Date();
    const start = subMonths(end, months);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const genderData = stats ? [
    { name: 'Hommes', value: stats.gender_breakdown?.hommes || 0 },
    { name: 'Femmes', value: stats.gender_breakdown?.femmes || 0 },
    { name: 'Enfants', value: stats.gender_breakdown?.enfants || 0 },
  ] : [];

  const statusData = stats ? [
    { name: 'Nouveaux', value: stats.status_breakdown?.nouveaux || 0 },
    { name: 'Membres', value: stats.status_breakdown?.membres || 0 },
    { name: 'Serviteurs', value: stats.status_breakdown?.serviteurs || 0 },
    { name: 'Invités', value: stats.status_breakdown?.invites || 0 },
  ] : [];

  const trendData = stats?.trend || [];
  const perActivity = stats?.per_activity || [];

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Statistiques</h1>

      {/* Filter bar */}
      <div style={styles.filterBar}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[
            { label: 'Ce mois', months: 0 },
            { label: '3 mois', months: 3 },
            { label: '6 mois', months: 6 },
            { label: '1 an', months: 12 },
          ].map((p) => (
            <button
              key={p.label}
              style={{
                padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)',
                backgroundColor: 'var(--page-bg)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                color: 'var(--text-primary)', fontFamily: 'inherit',
              }}
              onClick={() => setPreset(p.months)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date de début</label>
          <input type="date" style={styles.input} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date de fin</label>
          <input type="date" style={styles.input} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Type d'activité</label>
          <select style={styles.select} value={activityType} onChange={(e) => setActivityType(e.target.value)}>
            <option value="">Tous les types</option>
            {activityTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <button style={styles.btnPrimary} onClick={fetchStats} disabled={loading}>
          {loading ? 'Chargement...' : 'Appliquer'}
        </button>
        <button
          style={{
            ...styles.btnPrimary,
            backgroundColor: perActivity.length ? '#059669' : '#94a3b8',
            cursor: perActivity.length ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit',
          }}
          onClick={exportCSV}
          disabled={!perActivity.length}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exporter CSV
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <LoadingSpinner size={40} label="Chargement des statistiques..." />
        </div>
      ) : stats ? (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(auto-fill, minmax(160px,1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard title="Activités" value={stats.total_activities} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            )} color="blue" subtitle="sur la période" />
            <StatCard title="Total présences" value={stats.total_attendance} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            )} color="green" subtitle="personnes enregistrées" />
            <StatCard title="Nouveaux" value={stats.status_breakdown?.nouveaux || 0} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            )} color="purple" subtitle="nouveaux visiteurs" />
            <StatCard title="Hommes" value={stats.gender_breakdown?.hommes || 0} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/>
              </svg>
            )} color="blue" />
            <StatCard title="Femmes" value={stats.gender_breakdown?.femmes || 0} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/><path d="M12 14v7"/><path d="M9 18h6"/>
                <path d="M20 21a8 8 0 0 0-8-7"/>
              </svg>
            )} color="orange" />
            <StatCard title="Enfants" value={stats.gender_breakdown?.enfants || 0} Icon={() => (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="4"/><path d="M5.5 21a7 7 0 0 1 13 0"/>
              </svg>
            )} color="yellow" />
          </div>

          {/* Pie charts */}
          <div style={{ ...styles.twoCol, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Répartition par genre</div>
              {genderData.every((d) => d.value === 0) ? (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucune donnée disponible.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={100} labelLine={false} label={CUSTOM_LABEL}>
                      {genderData.map((_, i) => <Cell key={i} fill={GENDER_COLORS[i]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={(v) => [`${v} personnes`]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Répartition par statut</div>
              {statusData.every((d) => d.value === 0) ? (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucune donnée disponible.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={100} labelLine={false} label={CUSTOM_LABEL}>
                      {statusData.map((_, i) => <Cell key={i} fill={STATUS_COLORS_PIE[i]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={(v) => [`${v} personnes`]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Répartition par famille */}
          {churchSettings.use_families && familyStats && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Répartition par Famille RCC</div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}>{familyStats.total_families}</strong> famille(s)
                </div>
                {familyStats.no_family_count > 0 && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}>{familyStats.no_family_count}</strong> membre(s) sans famille
                  </div>
                )}
              </div>
              {familyStats.breakdown.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucune donnée disponible.</p>
              ) : (
                <div style={{ ...styles.twoCol, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={familyStats.breakdown} dataKey="count" nameKey="family_name" cx="50%" cy="50%"
                        outerRadius={100} labelLine={false} label={makeFamilyLabel(familyStats.breakdown)}>
                        {familyStats.breakdown.map((_, i) => <Cell key={i} fill={FAMILY_COLORS[i % FAMILY_COLORS.length]} />)}
                      </Pie>
                      <Legend />
                      <Tooltip formatter={(v) => [`${v} membres`]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                    {familyStats.breakdown.map((f, i) => (
                      <div key={f.family_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: FAMILY_COLORS[i % FAMILY_COLORS.length] }} />
                          {f.family_name}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{f.count} ({Math.round(f.percent)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trend chart */}
          {trendData.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Évolution des présences</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => v.length > 14 ? v.slice(0, 13) + '…' : v} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip formatter={(v) => [`${v} présences`, 'Total']} labelStyle={{ fontWeight: 600 }} />
                  <Line type="monotone" dataKey="count" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 4, fill: '#1e40af' }} name="Présences" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Bar chart comparison */}
          {perActivity.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Comparaison par activité (H/F/E)</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={perActivity} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => v.length > 12 ? v.slice(0, 11) + '…' : v} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip labelStyle={{ fontWeight: 600 }} />
                  <Legend />
                  <Bar dataKey="hommes" fill="#3b82f6" name="Hommes" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="femmes" fill="#ec4899" name="Femmes" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="enfants" fill="#f59e0b" name="Enfants" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per activity table */}
          {perActivity.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Détail par activité</div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ ...styles.tableHead, minWidth: mob ? '400px' : 'auto' }}>
                <span>Activité</span><span>Total</span><span>H</span><span>F</span><span>E</span>
              </div>
              {perActivity.map((a, idx) => (
                <div key={a.id} style={{ ...styles.tableRow(idx), minWidth: mob ? '400px' : 'auto' }}>
                  <span>
                    <div style={{ fontWeight: 600 }}>{a.name}</div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{a.date}{a.activity_type ? ` · ${a.activity_type}` : ''}</div>
                  </span>
                  <span style={{ fontWeight: 700, color: '#1e40af' }}>{a.total}</span>
                  <span style={{ color: '#3b82f6', fontWeight: 600 }}>{a.hommes}</span>
                  <span style={{ color: '#ec4899', fontWeight: 600 }}>{a.femmes}</span>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>{a.enfants}</span>
                </div>
              ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <div style={{ marginBottom: '12px' }}><BarChartIcon size={48} color="#94a3b8" /></div>
          <p>Aucune donnée disponible pour cette période.</p>
        </div>
      )}
    </div>
  );
}
